<script setup lang="ts">
import { ref, watch, onMounted, computed, nextTick } from 'vue';
import { useAddresses, type Address } from '~/composables/useAddresses';
import { State, City } from "country-state-city";
import { validatePincodeWithState } from "~/utils/shippingCalculator";
import Button from "~/components/atoms/Button.vue";
import TextInput from "~/components/ui/TextInput.vue";
import SelectInput from "~/components/ui/SelectInput.vue";

const props = defineProps<{
  modelValue: {
    street: string;
    city: string;
    state: string;
    zip: string;
    country: string;
  };
  errors?: Record<string, string>;
}>();

const emit = defineEmits(['update:modelValue', 'update:errors']);

const { addresses, isLoading, fetchAddresses, addAddress, updateAddress } = useAddresses();

const isAddingNew = ref(false);
const isSaving = ref(false);
const selectedAddressId = ref<string | null>(null);
const settingDefaultId = ref<string | null>(null);

const newAddress = ref({
  name: '',
  zip: '',
  street: '',
  state: '',
  city: '',
  country: 'India',
  is_default: true,
});

const formErrors = ref<Record<string, string>>({});

// Load addresses on mount
onMounted(async () => {
  await fetchAddresses();
  if (addresses.value.length > 0) {
    // Select default or first
    const defaultAddr = addresses.value.find(a => a.is_default) || addresses.value[0];
    selectAddress(defaultAddr);
  }
});

const selectAddress = (addr: Address) => {
  selectedAddressId.value = addr.id;
  isAddingNew.value = false;
  emit('update:modelValue', {
    name: addr.name,
    street: addr.street,
    city: addr.city,
    state: addr.state,
    zip: addr.zip,
    country: addr.country,
  });
  // Clear related errors
  const newErrors = { ...props.errors };
  delete newErrors.street;
  delete newErrors.city;
  delete newErrors.state;
  delete newErrors.zip;
  emit('update:errors', newErrors);
};

const handleSetDefault = async (addr: Address) => {
  settingDefaultId.value = addr.id;
  try {
    await updateAddress(addr.id, { is_default: true });
    // Add a tiny delay so the user actually sees the loader if the network is too fast
    await new Promise(resolve => setTimeout(resolve, 400));
  } finally {
    settingDefaultId.value = null;
  }
};

// --- Form logic for new address ---
const indianStates = computed(() => {
  return State.getStatesOfCountry("IN").map(state => ({
    label: state.name,
    value: state.name,
  }));
});

const getStateIsoCode = (stateName: string) => {
  return State.getStatesOfCountry("IN").find(s => s.name === stateName)?.isoCode || "";
};

const indianCities = computed(() => {
  if (!newAddress.value.state) return [];
  const isoCode = getStateIsoCode(newAddress.value.state);
  return City.getCitiesOfState("IN", isoCode).map(city => {
    const cityName = city.name === 'Gurgaon' ? 'Gurugram' : city.name;
    return {
      label: cityName,
      value: cityName,
    };
  });
});

watch(() => newAddress.value.state, () => {
  newAddress.value.city = "";
});

watch(() => newAddress.value.zip, async (newZip) => {
  if (newZip && newZip.length === 6) {
    try {
      const res = await $fetch<any>(`https://api.postalpincode.in/pincode/${newZip}`);
      if (res && res[0] && res[0].Status === 'Success') {
        const postOffice = res[0].PostOffice[0];
        const stateName = postOffice.State;
        let districtName = postOffice.District.replace(/\(.*?\)/g, '').trim();

        const stateObj = State.getStatesOfCountry("IN").find(s => s.name.toLowerCase() === stateName.toLowerCase());
        
        if (stateObj) {
          newAddress.value.state = stateObj.name;
          
          await nextTick();
          
          let cityObj = City.getCitiesOfState("IN", stateObj.isoCode).find(c => c.name.toLowerCase() === districtName.toLowerCase());
          
          if (!cityObj && postOffice.Region) {
             const regionName = postOffice.Region.replace(/\(.*?\)/g, '').trim();
             cityObj = City.getCitiesOfState("IN", stateObj.isoCode).find(c => c.name.toLowerCase() === regionName.toLowerCase());
          }
          
          if (cityObj) {
             const cityName = cityObj.name === 'Gurgaon' ? 'Gurugram' : cityObj.name;
             newAddress.value.city = cityName;
          }
        }
      }
    } catch(e) {
      console.error('Failed to fetch pincode info', e);
    }
  }
});

const saveNewAddress = async () => {
  formErrors.value = {};
  if (!newAddress.value.name) formErrors.value.name = "Address Label is required";
  if (!newAddress.value.zip || newAddress.value.zip.length !== 6) {
    formErrors.value.zip = "Valid 6-digit Zip required";
  } else if (newAddress.value.state) {
    const mismatch = validatePincodeWithState(newAddress.value.zip, newAddress.value.state);
    if (!mismatch.isValid) {
      formErrors.value.zip = mismatch.error || "Pincode does not match selected state";
    }
  }
  if (!newAddress.value.street) formErrors.value.street = "Street is required";
  if (!newAddress.value.state) formErrors.value.state = "State is required";
  if (!newAddress.value.city) formErrors.value.city = "City is required";

  if (Object.keys(formErrors.value).length > 0) return;

  isSaving.value = true;
  try {
    const saved = await addAddress({
      name: newAddress.value.name,
      street: newAddress.value.street,
      city: newAddress.value.city,
      state: newAddress.value.state,
      zip: newAddress.value.zip,
      country: newAddress.value.country,
      is_default: newAddress.value.is_default
    });
    selectAddress(saved);
    // Reset form
    newAddress.value = { name: '', zip: '', street: '', state: '', city: '', country: 'India', is_default: true };
  } catch (e) {
    console.error(e);
  } finally {
    isSaving.value = false;
  }
};
</script>

<template>
  <div class="flex flex-col gap-4" data-error-field="address">
    <h5 class="font-bold text-xs uppercase tracking-widest border-b pb-1">Shipping Address</h5>
    
    <div v-if="isLoading" class="flex justify-center py-4">
      <Icon name="lucide:loader-2" class="w-6 h-6 animate-spin text-gray-400" />
    </div>
    
    <template v-else>
      <!-- Address List -->
      <div v-if="!isAddingNew" class="flex flex-col gap-3">
        <div 
          v-for="addr in addresses" 
          :key="addr.id"
          class="border rounded-[6px] p-4 cursor-pointer transition relative overflow-hidden"
          :class="selectedAddressId === addr.id ? 'border-black bg-gray-50' : 'border-gray-200 hover:border-gray-400'"
          @click="selectAddress(addr)"
        >
          <!-- Active Indicator -->
          <div v-if="selectedAddressId === addr.id" class="absolute top-0 left-0 w-1 h-full bg-black"></div>
          
          <div class="flex justify-between items-start mb-3 pl-1 gap-4">
            <span class="font-bold text-sm text-gray-900 leading-snug flex-1 capitalize">{{ addr.name || '' }}</span>
            <div class="flex items-center gap-2 shrink-0 pt-0.5">
              <span v-if="addr.is_default" class="text-[10px] uppercase tracking-wider font-bold bg-gray-200 text-gray-700 px-2 py-0.5 rounded">Default</span>
              <Icon v-if="selectedAddressId === addr.id" name="lucide:check-circle-2" class="w-5 h-5 text-black" />
            </div>
          </div>
          <p class="text-xs text-gray-600 leading-relaxed pl-1 max-w-[90%]">
            {{ addr.street }}<br/>
            {{ addr.city }}, {{ addr.state }} - {{ addr.zip }}<br/>
            {{ addr.country }}
          </p>
          <div class="flex justify-end items-center mt-3 pl-1">
            <button 
              v-if="!addr.is_default" 
              @click.stop="handleSetDefault(addr)"
              class="text-xs font-medium text-gray-500 hover:text-black flex items-center gap-1"
              :disabled="settingDefaultId === addr.id"
            >
              <Icon v-if="settingDefaultId === addr.id" name="lucide:loader-2" class="w-3 h-3 animate-spin" />
              <span class="underline underline-offset-2">{{ settingDefaultId === addr.id ? 'Setting...' : 'Set as default' }}</span>
            </button>
          </div>
        </div>
        
        <Button variant="secondary" class="w-full justify-center py-2 mt-2" @click="isAddingNew = true">
          <Icon name="lucide:plus" class="w-4 h-4 mr-1" /> Add New Address
        </Button>
      </div>
      
      <!-- Add New Address Form -->
      <div v-else class="flex flex-col gap-4 bg-gray-50 p-4 border border-gray-200 rounded-[6px]">
        <div class="flex justify-between items-center mb-1">
          <h6 class="font-bold text-sm">Add New Address</h6>
          <button v-if="addresses.length > 0" @click="isAddingNew = false" class="text-xs text-gray-500 hover:text-black">Cancel</button>
        </div>
        
        <UFormField label="Address Label" :error="formErrors.name">
          <TextInput v-model="newAddress.name" placeholder="e.g. Home, Office" :error="!!formErrors.name" />
        </UFormField>

        <UFormField label="Zip Code" :error="formErrors.zip">
          <TextInput v-model="newAddress.zip" placeholder="400001" numeric-only maxlength="6" :error="!!formErrors.zip" />
        </UFormField>
        <UFormField label="Street Address" :error="formErrors.street">
          <TextInput v-model="newAddress.street" placeholder="Building, Street, Area" :error="!!formErrors.street" />
        </UFormField>
        <div class="grid grid-cols-2 gap-4">
          <UFormField label="State" :error="formErrors.state">
            <SelectInput v-model="newAddress.state" placeholder="Select State" :options="indianStates" :error="!!formErrors.state" />
          </UFormField>
          <UFormField label="City" :error="formErrors.city">
            <SelectInput v-model="newAddress.city" placeholder="Select City" :options="indianCities" :error="!!formErrors.city" :disabled="!newAddress.state" />
          </UFormField>
        </div>
        <UFormField label="Country">
          <TextInput v-model="newAddress.country" disabled />
        </UFormField>
        
        <label class="flex items-center gap-2 cursor-pointer mt-2">
          <input type="checkbox" v-model="newAddress.is_default" class="w-4 h-4 rounded-none border border-gray-300 accent-black cursor-pointer">
          <span class="text-sm text-gray-700">Save as default address</span>
        </label>
        
        <Button variant="primary" class="w-full justify-center py-2 mt-2" :loading="isSaving" @click="saveNewAddress">
          Save Address
        </Button>
      </div>
      
      <!-- Parent level validation error (if they try to submit main form without selecting/adding an address) -->
      <div v-if="errors?.street || errors?.zip || errors?.city || errors?.state" class="text-red-500 text-xs mt-1">
        Please select or add a shipping address.
      </div>
    </template>
  </div>
</template>
